import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

import "../styles/AdminGuestBook.css";
import { GUESTBOOK_API } from "../config/api";
import { showConfirm } from "../utils/customDialog";

const BASE_URL = GUESTBOOK_API;

function AdminGuestBook() {
  const [entries, setEntries] = useState([]);
  const prevRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);

  // ================= SEARCH & FILTERS =================
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // ================= PAGINATION =================
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // ================= UNLINK OTHER ADMIN CSS =================
  

  /* ================= POPUP ================= */
  const showPopup = (msg) => {
    const div = document.createElement("div");

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #58181f;
        color: #fff;
        padding: 14px 16px;
        border-radius: 10px;
        z-index: 99999;
        box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        font-size: 14px;
        max-width: 280px;
      ">
        ${msg}
      </div>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      div.remove();
    }, 3000);
  };

  /* ================= FETCH ================= */
  const fetchEntries = useCallback(async () => {
    try {
      const res = await fetch(BASE_URL);
      const data = await res.json();

      if (data?.status === "success") {
        const newData = Array.isArray(data.data)
          ? data.data
          : [];

        // Detect only genuinely new entries
        if (prevRef.current.length > 0) {
          newData.forEach((newItem) => {
            const exists = prevRef.current.find(
              (e) => e.id === newItem.id
            );

            if (!exists) {
              showPopup(
                `🆕 New Guest Entry: ${newItem.name}`
              );
            }
          });
        }

        setEntries(newData);
        prevRef.current = newData;
      } else {
        setEntries([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================= INIT + AUTO REFRESH ================= */
  useEffect(() => {
    fetchEntries();

    const interval = setInterval(() => {
      fetchEntries();
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchEntries]);

  // ================= FILTER LOGIC =================
  const filteredEntries = entries.filter((entry) => {
    const searchText = search.toLowerCase();

    const matchSearch =
      entry.name?.toLowerCase().includes(searchText) ||
      entry.email?.toLowerCase().includes(searchText) ||
      entry.message?.toLowerCase().includes(searchText);

    const matchStatus =
      statusFilter === "all" ||
      entry.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchSearch && matchStatus;
  });

  // ================= PAGINATION LOGIC =================
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredEntries.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );
  const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // ================= RESET PAGE ON FILTER CHANGE =================
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  /* ================= UPDATE STATUS ================= */
  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(
        `${BASE_URL}?action=updateStatus`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            status,
          }),
        }
      );

      const result = await res.json();

      if (result?.status === "success") {
        setEntries((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, status }
              : item
          )
        );

        showPopup(
          `✔ ${status.toUpperCase()} updated`
        );
      } else {
        showPopup("❌ Update failed");
      }
    } catch (err) {
      console.error(
        "Status update error:",
        err
      );

      showPopup("❌ Server error");
    }
  };

  /* ================= DELETE ================= */
  const deleteEntry = async (id) => {
    const ok = await showConfirm({
      title: "Delete Guest Book Entry",
      message: "Are you sure you want to delete this guest book entry?",
      subMessage: "This will remove the comment from the public guest book.",
      confirmText: "Delete Entry",
      type: "danger"
    });
    if (!ok) {
      return;
    }

    try {
      const res = await fetch(
        `${BASE_URL}?action=delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        }
      );

      const result = await res.json();

      if (result?.status === "success") {
        setEntries((prev) =>
          prev.filter(
            (item) => item.id !== id
          )
        );

        showPopup(
          "🗑 Deleted successfully"
        );
      } else {
        showPopup("❌ Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showPopup("❌ Server error");
    }
  };

  /* ================= EDIT ================= */
  const openEdit = (item) => {
    setEditData(item);
  };

  const saveEdit = async () => {
    if (!editData) return;

    try {
      const res = await fetch(
        `${BASE_URL}?action=edit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData),
        }
      );

      const result = await res.json();

      if (result?.status === "success") {
        showPopup(
          "✏ Updated successfully"
        );

        setEditData(null);
        fetchEntries();
      } else {
        showPopup("❌ Update failed");
      }
    } catch (err) {
      console.error("Edit error:", err);
      showPopup("❌ Server error");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="admin-container guestbook-admin-container">

      {/* ================= HEADER ================= */}
      <div className="admin-header">
        <div className="admin-header-title">
          <h2>Guest Book Moderation</h2>
          <p>Monitor, moderate, and manage guest book testimonials and user submissions</p>
        </div>
      </div>

      {/* ================= STATS ================= */}
      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-info">
            <h4>Total Guest Entries</h4>
            <p>{entries.length}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-chat-left-quote-fill"></i>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-info">
            <h4>Approved Testimonials</h4>
            <p>{entries.filter(e => e.status === "approved").length}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-check-circle-fill"></i>
          </div>
        </div>

        <div className="stat-card stat-revenue">
          <div className="stat-info">
            <h4>Pending Reviews</h4>
            <p>{entries.filter(e => e.status === "pending").length}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-clock-fill"></i>
          </div>
        </div>
      </div>

      {/* ================= FILTERS DECK ================= */}
      <div className="admin-filters-deck">
        <div className="filter-search-group">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="admin-search-input"
            style={{ paddingLeft: "48px" }}
            placeholder="Search by name, email, or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-controls-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <section style={{ background: "transparent", border: "none", boxShadow: "none", padding: 0 }}>
        {loading ? (
          <div className="admin-loading">
            <h2>Loading entries...</h2>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="admin-empty-state" style={{ background: "#fff", borderRadius: "12px", border: "1px solid var(--border-color)", padding: "40px", textAlign: "center" }}>
            <i className="bi bi-chat-left-x" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
            <h3 style={{ marginTop: "12px", color: "#64748b" }}>No Entries Found</h3>
            <p style={{ color: "#94a3b8" }}>We couldn't find any guest book entries matching your filter criteria.</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Location / City</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Submitted On</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentEntries.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.name}</strong>
                      </td>
                      <td>{item.email}</td>
                      <td>
                        <span className="status-pill processing" style={{ padding: "4px 8px", fontSize: "11px", fontWeight: "600" }}>
                          {item.city || "N/A"}
                        </span>
                      </td>
                      <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.message}>
                        {item.message}
                      </td>
                      <td>
                        <span className={`status-pill ${item.status === 'approved' ? 'success' : item.status === 'pending' ? 'pending' : 'failed'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })
                          : "N/A"}
                      </td>
                      <td>
                        <div className="table-actions-container" style={{ justifyContent: "center" }}>
                          <select
                            className="status-dropdown"
                            value={item.status}
                            onChange={(e) => updateStatus(item.id, e.target.value)}
                            style={{
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid var(--border-color)",
                              fontSize: "12px",
                              marginRight: "8px",
                              outline: "none",
                              cursor: "pointer"
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>

                          <button
                            className="btn-table-action action-view-expand"
                            onClick={() => openEdit(item)}
                            title="Edit Entry"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button
                            className="btn-table-action action-delete"
                            onClick={() => deleteEntry(item.id)}
                            title="Delete Entry"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ================= PAGINATION ================= */}
            {filteredEntries.length > entriesPerPage && (
              <div className="pagination">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Prev
                </button>

                <span>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* EDIT MODAL PORTAL */}
      {editData && createPortal(
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(4px)"
        }}>
          <div style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "16px",
            width: "95%",
            maxWidth: "500px",
            border: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-xl)",
            color: "var(--text-dark)",
            textAlign: "left"
          }}>
            <h3 style={{ 
              color: "var(--secondary-color)", 
              marginBottom: "20px", 
              fontWeight: "bold", 
              borderBottom: "2px solid var(--border-color)", 
              paddingBottom: "12px", 
              marginTop: 0,
              fontSize: "18px",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <i className="bi bi-pencil-square"></i> Edit Guest Entry
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", color: "var(--text-dark)" }}>Full Name</label>
                <input
                  type="text"
                  value={editData.name || ""}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", color: "var(--text-dark)" }}>Email Address</label>
                <input
                  type="email"
                  value={editData.email || ""}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", color: "var(--text-dark)" }}>Location / City</label>
                <input
                  type="text"
                  value={editData.city || ""}
                  onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", color: "var(--text-dark)" }}>Message / Testimonial</label>
                <textarea
                  value={editData.message || ""}
                  onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                    minHeight: "100px"
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "2.5px solid var(--border-color)", paddingTop: "20px", marginTop: "20px" }}>
              <button
                type="button"
                onClick={() => setEditData(null)}
                style={{
                  padding: "10px 20px",
                  border: "1.5px solid var(--border-color)",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: "var(--text-dark)",
                  cursor: "pointer",
                  fontWeight: "750",
                  fontSize: "14px"
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-maroon"
                onClick={saveEdit}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "#5d1d2b",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: "750",
                  fontSize: "14px"
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AdminGuestBook;
